import { useCallback, useState } from 'react'
import { Formik, Field, Form, FormikErrors } from 'formik'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import { isEmpty } from 'lodash'
import { toast } from 'react-toastify'

import Modal from 'components/Modal'
import TextBox from 'components/TextBox'
import CheckAccordion from 'components/CheckAccordion'

import { PERMISSIONS_APPS } from 'resources/PERMISSIONS_APPS'
import { useCreateRoleMutation } from 'store/porticoApi/roles'
import { useGetOrganizationsQuery } from 'store/porticoApi/organizations/organizations'
import { useCreatePermissionMutation } from 'store/porticoApi/permissions'
import { TAppTypesDisplaySubsitutes } from 'types/apps'
import { TActions } from 'types/actions'
import { setAddRoleValues } from 'utils/rolesPermissions/setAddRoleValues'
import { convertActionToApiValue } from 'utils/rolesPermissions/convertAction'
import { convertDisplayAppNameToApi } from 'utils/rolesPermissions/convertAppNames'

type TReturnDropdownValue = {
  name: TAppTypesDisplaySubsitutes
  option: string
  isActiveCheckAccordion: boolean
}

type TFormValues = {
  name: string
  permissions: TReturnDropdownValue[]
}

const AddRole = () => {
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const navigate = useNavigate()

  const { data: orgnization } = useGetOrganizationsQuery()

  const [createRole] = useCreateRoleMutation()
  const [createPermission] = useCreatePermissionMutation()

  const initialValues: TFormValues = {
    name: '',
    permissions: [],
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
  })

  const disableUpdateButton = useCallback(
    (dirty: boolean, errors: FormikErrors<TFormValues>) => {
      const disableButton = !dirty || (showValidationErrors && !isEmpty(errors))

      return disableButton
    },
    [showValidationErrors]
  )

  const onClickSaveButton = useCallback(() => {
    setShowValidationErrors(true)
  }, [])

  const onCloseModal = () => navigate('/roles-permissions')

  const onSubmit = ({ name, permissions }: TFormValues) => {
    // we have to transform name and option to their substitutes in api
    const transformPermissionsValues = permissions?.map(
      ({ name, option, isActiveCheckAccordion }) => ({
        name: convertDisplayAppNameToApi(name),
        option: convertActionToApiValue(option as TActions),
        isActiveCheckAccordion,
      })
    )

    const organizationId = orgnization?.uuid as string

    createRole({ name, organizationId, appType: null })
      .unwrap()
      .then((response) => {
        const newPermissionParam = transformPermissionsValues?.map(({ name, option }) => ({
          subject: name,
          action: option,
          userRoleId: response.uuid,
        }))

        createPermission(newPermissionParam)
          .unwrap()
          .then(() => {
            navigate('/roles-permissions')
            toast.success('Success!')
          })
      })
  }

  return (
    <Modal title="Add Role" onClose={onCloseModal} className="-narrow">
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors, setFieldValue, values }) => (
            <Form className="item-settings">
              <section className="item-settings__section ">
                <div className="content-wrapper">
                  <Field
                    component={TextBox}
                    label="Role Name"
                    name="name"
                    showValidationErrors={showValidationErrors}
                    onChange={(value: string) => {
                      setFieldValue('name', value)
                    }}
                    value={values?.name}
                    error={errors?.name}
                  />
                </div>
              </section>
              <div className="item-settings__border-break"></div>
              <h3 className="header pt-mxs">Permissions</h3>
              <section>
                {PERMISSIONS_APPS?.map(
                  ({
                    name,
                    uuid,
                    subApps,
                    disabledCheckbox,
                    disabledDropdown,
                    actions,
                    isDropdown,
                    checked,
                  }) => (
                    <div className="content-wrapper -flags-wrapper -border-top-none" key={uuid}>
                      <Field
                        component={CheckAccordion}
                        label={name}
                        selectOptions={actions}
                        defaultSelectOption={{ name: 'VIEW', uuid: 'VIEW' }}
                        additionalAccordionOptions={subApps}
                        onChange={(value: TReturnDropdownValue) =>
                          setAddRoleValues(value, values?.permissions, setFieldValue)
                        }
                        disabledCheckbox={disabledCheckbox}
                        disabledDropdown={disabledDropdown}
                        isDropdown={isDropdown}
                        checked={checked}
                      />
                    </div>
                  )
                )}
              </section>
              <div className="btn-wrapper">
                <button
                  className="btn -update"
                  onClick={onClickSaveButton}
                  disabled={disableUpdateButton(dirty, errors)}
                  type="submit"
                >
                  Save
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default AddRole
